import {
  CampaignMessageTemplate,
  CampaignTemplate,
  CustomerCareOrg,
  Patient,
  PatientCampaign,
  PatientTreatment,
  SmsMessage,
  TreatmentCategory,
  TwilioMessagingService,
  TwilioPhoneNumber,
  TwilioSubAccount,
  TwilioTollFreeVerification,
} from '@models/customer-care';
import { IChatEvent } from '@models/spartacus/chat-event';
import { v4 as uuid } from 'uuid';

/**
 * localStorage-backed mock data layer for the Customer Care suite.
 *
 * IMPORTANT (security): the public feedback page is reached only via an opaque,
 * high-entropy per-organization `publicToken` — never the sequential integer
 * organizationId. `getOrgByPublicToken` is the only way to resolve an org from a
 * public URL, which prevents IDOR / enumeration of other businesses.
 */

const STORE_KEY = 'cc.store.v2';

/** Preset tones every campaign provides ready-made message templates for. */
export const TONE_PRESETS = ['Warm & Friendly', 'Professional & Caring', 'Concise & Direct'] as const;
export const DEFAULT_TONE = 'Warm & Friendly';

/** Mock helper: derive a tone-specific variant of a base (Warm & Friendly) message. */
function toToneVariant(content: string, tone: string): string {
  if (tone === DEFAULT_TONE) return content;
  const body = content
    .replace(/^Hi \[Name\]!\s*/i, '')
    .replace(/^Hey \[Name\],\s*/i, '')
    .replace(/^Hi \[Name\],\s*/i, '')
    .trim();
  if (tone === 'Professional & Caring') {
    return `Hi [Name], I hope you're doing well following your appointment. ${body.replace(/!/g, '.')} Please don't hesitate to reach out if you have any questions or concerns.`;
  }
  // Concise & Direct: keep the first sentence only, then a short call to action.
  const firstSentence = body.split(/(?<=[.!?])\s/)[0] ?? body;
  return `Hi [Name], ${firstSentence} Let us know if you need anything.`;
}

interface OrgBucket {
  patients: Patient[];
  patientTreatments: PatientTreatment[];
  chatEvents: IChatEvent[];
  smsMessages: SmsMessage[];
  patientCampaigns: PatientCampaign[];
  twilio: {
    subAccount?: TwilioSubAccount;
    messagingService?: TwilioMessagingService;
    phoneNumber?: TwilioPhoneNumber;
    verification?: TwilioTollFreeVerification;
  };
}

interface RootStore {
  orgs: Record<number, CustomerCareOrg>;
  treatmentCategories: TreatmentCategory[];
  campaignTemplates: CampaignTemplate[];
  campaignMessageTemplates: CampaignMessageTemplate[];
  byOrg: Record<number, OrgBucket>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function emptyBucket(): OrgBucket {
  return {
    patients: [],
    patientTreatments: [],
    chatEvents: [],
    smsMessages: [],
    patientCampaigns: [],
    twilio: {},
  };
}

function emptyStore(): RootStore {
  return { orgs: {}, treatmentCategories: [], campaignTemplates: [], campaignMessageTemplates: [], byOrg: {} };
}

function readStore(): RootStore {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return emptyStore();
    return { ...emptyStore(), ...(JSON.parse(raw) as RootStore) };
  } catch {
    return emptyStore();
  }
}

function writeStore(store: RootStore): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

function generatePublicToken(): string {
  // High-entropy, non-sequential token. Combine two uuids (sans dashes) for extra length.
  return `${uuid()}${uuid()}`.replace(/-/g, '');
}

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

const TREATMENT_SEED: Array<{ name: string; description: string }> = [
  { name: 'Botox Treatment', description: 'Aftercare, check-in, and retouch reminder' },
  { name: 'Filler Treatment', description: 'Aftercare, check-in, and retouch reminder' },
  { name: 'Hydrafacial Treatment', description: 'Aftercare, check-in, and next session reminder' },
  { name: 'Laser Treatment', description: 'Aftercare, check-in, and follow-up reminder' },
];

function ensureGlobalSeed(store: RootStore): void {
  if (store.treatmentCategories.length === 0) {
    store.treatmentCategories = TREATMENT_SEED.map((t) => ({
      id: uuid(),
      name: t.name,
      description: t.description,
      createdAt: nowIso(),
    }));
  }

  if (store.campaignTemplates.length === 0) {
    const byName = (name: string) => store.treatmentCategories.find((c) => c.name === name);
    const templates: Array<{
      type: CampaignTemplate['type'];
      categoryName: string;
      messages: Array<{ delayMinutes: number; content: string; tone?: string }>;
    }> = [
      {
        type: 'CUSTOMER_CARE',
        categoryName: 'Botox Treatment',
        messages: [
          {
            delayMinutes: 4 * 60,
            content:
              'Hi [Name]! Just checking in after your Botox. Remember not to lie down for the next 4 hours. How are you feeling?',
            tone: 'Warm & Friendly',
          },
          {
            delayMinutes: 10 * 24 * 60,
            content:
              'Hey [Name], just checking in. You might start seeing some initial results now! Keep avoiding any facial massages.',
            tone: 'Warm & Friendly',
          },
          {
            delayMinutes: 20 * 24 * 60,
            content:
              'Hi [Name]! You should be seeing the full effect of your Botox now. Let us know if you’re happy with the results or if you need a touch-up!',
            tone: 'Warm & Friendly',
          },
        ],
      },
      {
        type: 'CUSTOMER_CARE',
        categoryName: 'Filler Treatment',
        messages: [
          {
            delayMinutes: 4 * 60,
            content: 'Hi [Name]! Just checking in after your filler treatment. A little swelling is normal today.',
            tone: 'Warm & Friendly',
          },
          {
            delayMinutes: 7 * 24 * 60,
            content: 'Hey [Name], how is everything settling? Your final results should be showing now.',
            tone: 'Warm & Friendly',
          },
        ],
      },
      {
        type: 'CUSTOMER_CARE',
        categoryName: 'Hydrafacial Treatment',
        messages: [
          {
            delayMinutes: 24 * 60,
            content: 'Hi [Name]! Hope you’re glowing after your Hydrafacial. Remember to keep hydrated and use SPF.',
            tone: 'Warm & Friendly',
          },
          {
            delayMinutes: 21 * 24 * 60,
            content: 'Hey [Name], time to book your next Hydrafacial to keep that glow going!',
            tone: 'Warm & Friendly',
          },
        ],
      },
      {
        type: 'ENGAGEMENT',
        categoryName: 'Laser Treatment',
        messages: [
          {
            delayMinutes: 24 * 60,
            content:
              'Hi [Name]! Just checking in after your laser session. Avoid direct sun and keep the area moisturized.',
            tone: 'Warm & Friendly',
          },
          {
            delayMinutes: 30 * 24 * 60,
            content: 'Hey [Name], ready for your follow-up laser session? Let’s keep your results on track.',
            tone: 'Warm & Friendly',
          },
        ],
      },
    ];

    for (const tpl of templates) {
      const templateId = uuid();
      store.campaignTemplates.push({
        id: templateId,
        organizationId: null,
        type: tpl.type,
        treatmentCategoryId: byName(tpl.categoryName)?.id ?? null,
        name: tpl.categoryName,
        description: TREATMENT_SEED.find((t) => t.name === tpl.categoryName)?.description ?? null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      });
      // Every campaign ships a ready-made set of messages for each preset tone.
      for (const tone of TONE_PRESETS) {
        tpl.messages.forEach((m, idx) => {
          store.campaignMessageTemplates.push({
            id: uuid(),
            campaignTemplateId: templateId,
            content: toToneVariant(m.content, tone),
            tone,
            delayMinutes: m.delayMinutes,
            campaignIndex: idx,
            createdAt: nowIso(),
          });
        });
      }
    }
  }
}

const PATIENT_SEED: Array<{
  firstName: string;
  lastName: string;
  phoneNumber: string;
  treatments: string[];
  sinceDays: number;
  lastMessage: string;
  lastMessageDaysAgo: number;
}> = [
  {
    firstName: 'Sophie',
    lastName: 'Johnson',
    phoneNumber: '+12025550111',
    treatments: ['Botox Treatment', 'Filler Treatment'],
    sinceDays: 40,
    lastMessage: 'Hi Sophie! Just checkin after your botox treatment. How are you feeling?',
    lastMessageDaysAgo: 0,
  },
  {
    firstName: 'Liam',
    lastName: 'Carter',
    phoneNumber: '+12025550112',
    treatments: ['Botox Treatment'],
    sinceDays: 90,
    lastMessage: 'Hi Liam! How is the swelling? Remember that aftercare matters.',
    lastMessageDaysAgo: 1,
  },
  {
    firstName: 'Emma',
    lastName: 'Thompson',
    phoneNumber: '+12025550113',
    treatments: ['Hydrafacial Treatment', 'Filler Treatment'],
    sinceDays: 120,
    lastMessage: 'Hi Emma! Just checking in. You might start seeing results soon.',
    lastMessageDaysAgo: 3,
  },
  {
    firstName: 'Noah',
    lastName: 'Williams',
    phoneNumber: '+12025550114',
    treatments: ['Laser Treatment'],
    sinceDays: 60,
    lastMessage: 'Hey Noah! Just checkin in after your laser treatment.',
    lastMessageDaysAgo: 5,
  },
  {
    firstName: 'Olivia',
    lastName: 'Brown',
    phoneNumber: '+12025550115',
    treatments: ['Botox Treatment', 'Laser Treatment'],
    sinceDays: 200,
    lastMessage: 'Hi Olivia! You should be seeing the full effect of your treatment.',
    lastMessageDaysAgo: 14,
  },
  {
    firstName: 'Ava',
    lastName: 'Davis',
    phoneNumber: '+12025550116',
    treatments: ['Hydrafacial Treatment'],
    sinceDays: 300,
    lastMessage: 'Hey Ava, your filler should be fully settled by now.',
    lastMessageDaysAgo: 30,
  },
];

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function seedOrgBucket(store: RootStore, organizationId: number): void {
  const bucket = emptyBucket();
  const catByName = (name: string) => store.treatmentCategories.find((c) => c.name === name);

  for (const seed of PATIENT_SEED) {
    const patientId = uuid();
    const createdAt = daysAgoIso(seed.sinceDays);
    bucket.patients.push({
      id: patientId,
      organizationId,
      phoneNumber: seed.phoneNumber,
      firstName: seed.firstName,
      lastName: seed.lastName,
      email: null,
      pronouns: null,
      marketingConsent: true,
      messagingConsent: true,
      createdAt,
      updatedAt: createdAt,
    });

    // PATIENT_ADDED event
    bucket.chatEvents.push({
      id: uuid(),
      patientId,
      eventType: 'PATIENT_ADDED',
      payload: JSON.stringify({ title: `${seed.firstName} ${seed.lastName} added` }),
      sourceType: undefined,
      sourceId: undefined,
      occurredAt: createdAt,
      createdAt,
      updatedAt: createdAt,
    });

    // Treatments + treatment chat events
    seed.treatments.forEach((tName, idx) => {
      const cat = catByName(tName);
      if (!cat) return;
      const appliedAt = daysAgoIso(seed.sinceDays - idx * 2 > 0 ? seed.sinceDays - idx * 2 : 1);
      const treatmentId = uuid();
      bucket.patientTreatments.push({
        id: treatmentId,
        patientId,
        treatmentCategoryId: cat.id,
        appliedAt,
        notes: null,
        feedbackRating: null,
        feedbackNotes: null,
        feedbackAt: null,
        createdAt: appliedAt,
      });
      bucket.chatEvents.push({
        id: uuid(),
        patientId,
        eventType: 'TREATMENT',
        payload: JSON.stringify({ treatmentName: tName, title: tName }),
        sourceType: 'TREATMENT',
        sourceId: treatmentId,
        occurredAt: appliedAt,
        createdAt: appliedAt,
        updatedAt: appliedAt,
      });
    });

    // Last outbound SMS
    const lastAt = daysAgoIso(seed.lastMessageDaysAgo);
    const smsId = uuid();
    bucket.smsMessages.push({
      id: smsId,
      patientId,
      patientCampaignId: null,
      content: seed.lastMessage,
      campaignIndex: null,
      scheduledAt: null,
      twilioSid: `SM${uuid().replace(/-/g, '')}`,
      direction: 'OUTBOUND',
      status: 'DELIVERED',
      sentAt: lastAt,
      deliveredAt: lastAt,
      createdAt: lastAt,
      updatedAt: lastAt,
    });
    bucket.chatEvents.push({
      id: uuid(),
      patientId,
      eventType: 'SMS_OUTBOUND',
      payload: JSON.stringify({ text: seed.lastMessage }),
      sourceType: 'SMS',
      sourceId: smsId,
      occurredAt: lastAt,
      createdAt: lastAt,
      updatedAt: lastAt,
    });
  }

  store.byOrg[organizationId] = bucket;
}

function ensureOrgTemplates(store: RootStore, organizationId: number): void {
  const orgTemplates = store.campaignTemplates.filter((t) => t.organizationId === organizationId);
  if (orgTemplates.length > 0) return;

  const globalTemplates = store.campaignTemplates.filter((t) => t.organizationId == null);
  for (const gt of globalTemplates) {
    const newTemplateId = uuid();
    store.campaignTemplates.push({
      ...gt,
      id: newTemplateId,
      organizationId,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    });

    const globalMessages = store.campaignMessageTemplates.filter((m) => m.campaignTemplateId === gt.id);
    for (const gm of globalMessages) {
      store.campaignMessageTemplates.push({
        ...gm,
        id: uuid(),
        campaignTemplateId: newTemplateId,
        createdAt: nowIso(),
      });
    }
  }
}

/**
 * Ensures global + per-org seed data exists, and that the org has a stable opaque
 * public token. Idempotent — safe to call on every read.
 */
function ensureOrg(organizationId: number, name: string): RootStore {
  const store = readStore();
  ensureGlobalSeed(store);

  if (!store.orgs[organizationId]) {
    store.orgs[organizationId] = {
      organizationId,
      name: name || `Organization ${organizationId}`,
      publicToken: generatePublicToken(),
    };
  } else if (name && store.orgs[organizationId].name !== name) {
    store.orgs[organizationId].name = name;
  }

  if (!store.byOrg[organizationId]) {
    seedOrgBucket(store, organizationId);
  }

  ensureOrgTemplates(store, organizationId);

  writeStore(store);
  return store;
}

function getBucket(store: RootStore, organizationId: number): OrgBucket {
  if (!store.byOrg[organizationId]) store.byOrg[organizationId] = emptyBucket();
  return store.byOrg[organizationId];
}

// ---------------------------------------------------------------------------
// Internal accessors used by the API modules
// ---------------------------------------------------------------------------

export const mockStore = {
  ensureOrg,
  readStore,
  writeStore,
  getBucket,
  nowIso,
  uuid,
  generatePublicToken,

  /** Resolve an org strictly by opaque public token. Returns null for unknown tokens. */
  getOrgByPublicToken(token: string): CustomerCareOrg | null {
    if (!token) return null;
    const store = readStore();
    const match = Object.values(store.orgs).find((o) => o.publicToken === token);
    return match ?? null;
  },
};
